import React, { useState, useEffect } from 'react';
import { X, Save, RotateCcw, Settings, Plus, Trash2, Palette, Wrench, MapPin, Edit3 } from 'lucide-react';
import { UserSettings, BoothSetting, SavedLocation, saveUserSettings, resetUserSettings } from './config';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userSettings: UserSettings;
  onSettingsUpdate: (settings: UserSettings) => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  userSettings,
  onSettingsUpdate,
}) => {
  const [localSettings, setLocalSettings] = useState<UserSettings>(userSettings);
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedBooth, setExpandedBooth] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'booths' | 'appearance' | 'locations'>('booths');
  const [editingLocation, setEditingLocation] = useState<string | null>(null);
  const [editLocationName, setEditLocationName] = useState('');
  const [editLocationAddress, setEditLocationAddress] = useState('');

  useEffect(() => {
    setLocalSettings(userSettings);
    setHasChanges(false);
  }, [userSettings, isOpen]);

  const handleBoothChange = (boothKey: string, field: keyof BoothSetting, value: string | number) => {
    const newSettings = {
      ...localSettings,
      booths: {
        ...localSettings.booths,
        [boothKey]: {
          ...localSettings.booths[boothKey],
          [field]: value,
        },
      },
    };
    setLocalSettings(newSettings);
    setHasChanges(true);
  };

  const handleBackgroundColorChange = (color: string) => {
    const newSettings = {
      ...localSettings,
      backgroundColor: color,
    };
    setLocalSettings(newSettings);
    setHasChanges(true);
  };

  const startEditingLocation = (location: SavedLocation) => {
    setEditingLocation(location.id);
    setEditLocationName(location.name);
    setEditLocationAddress(location.address);
  };

  const saveLocationEdit = () => {
    if (!editingLocation) return;
    
    const newSettings = {
      ...localSettings,
      savedLocations: localSettings.savedLocations.map(loc => 
        loc.id === editingLocation 
          ? { ...loc, name: editLocationName.trim(), address: editLocationAddress.trim() }
          : loc
      )
    };
    setLocalSettings(newSettings);
    setHasChanges(true);
    setEditingLocation(null);
    setEditLocationName('');
    setEditLocationAddress('');
  };

  const cancelLocationEdit = () => {
    setEditingLocation(null);
    setEditLocationName('');
    setEditLocationAddress('');
  };

  const deleteLocation = (locationId: string) => {
    const newSettings = {
      ...localSettings,
      savedLocations: localSettings.savedLocations.filter(loc => loc.id !== locationId)
    };
    setLocalSettings(newSettings);
    setHasChanges(true);
  };

  const addNewBooth = () => {
    const newKey = `booth_${Date.now()}`;
    const newSettings = {
      ...localSettings,
      booths: {
        ...localSettings.booths,
        [newKey]: {
          name: "New Booth",
          setupTime: 45,
          breakdownTime: 30,
        },
      },
    };
    setLocalSettings(newSettings);
    setHasChanges(true);
    setExpandedBooth(newKey);
  };

  const deleteBooth = (boothKey: string) => {
    if (Object.keys(localSettings.booths).length <= 1) {
      alert("You must have at least one booth configuration.");
      return;
    }
    
    const { [boothKey]: deleted, ...remainingBooths } = localSettings.booths;
    const newSettings = {
      ...localSettings,
      booths: remainingBooths,
    };
    setLocalSettings(newSettings);
    setHasChanges(true);
    if (expandedBooth === boothKey) {
      setExpandedBooth(null);
    }
  };

  const handleSave = () => {
    saveUserSettings(localSettings);
    onSettingsUpdate(localSettings);
    setHasChanges(false);
    onClose();
  };

  const handleReset = () => {
    const resetSettings = resetUserSettings();
    setLocalSettings(resetSettings);
    onSettingsUpdate(resetSettings);
    setHasChanges(false);
  };

  const handleClose = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        setLocalSettings(userSettings);
        setHasChanges(false);
        onClose();
      }
    } else {
      onClose();
    }
  };

  const backgroundColorPresets = [
    { name: 'White', color: '#ffffff' },
    { name: 'Light Blue', color: '#f0f9ff' },
    { name: 'Light Gray', color: '#f8fafc' },
    { name: 'Light Green', color: '#f0fdf4' },
    { name: 'Light Yellow', color: '#fefce8' },
    { name: 'Light Pink', color: '#fdf2f8' },
    { name: 'Light Purple', color: '#faf5ff' },
    { name: 'Light Orange', color: '#fff7ed' },
  ];

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '1rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        maxWidth: '40rem',
        width: '100%',
        margin: '0 1rem',
        maxHeight: '90vh',
        overflowY: 'auto',
        border: '2px solid #bfdbfe'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1.5rem 2rem',
          borderBottom: '2px solid #bfdbfe',
          backgroundColor: '#eff6ff',
          borderRadius: '1rem 1rem 0 0'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#1e40af',
            display: 'flex',
            alignItems: 'center',
            margin: 0
          }}>
            <Settings style={{ marginRight: '0.75rem' }} size={24} />
            Settings
          </h2>
          <button
            onClick={handleClose}
            style={{
              color: '#6b7280',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              borderRadius: '0.5rem',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#dbeafe'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '2px solid #bfdbfe',
          backgroundColor: '#f8fafc'
        }}>
          <button
            onClick={() => setActiveTab('booths')}
            style={{
              flex: 1,
              padding: '1rem 1.5rem',
              backgroundColor: activeTab === 'booths' ? '#eff6ff' : 'transparent',
              color: activeTab === 'booths' ? '#1e40af' : '#6b7280',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s ease',
              borderBottom: activeTab === 'booths' ? '3px solid #3b82f6' : '3px solid transparent'
            }}
          >
            <Wrench size={18} />
            Booth Settings
          </button>
          <button
            onClick={() => setActiveTab('locations')}
            style={{
              flex: 1,
              padding: '1rem 1.5rem',
              backgroundColor: activeTab === 'locations' ? '#eff6ff' : 'transparent',
              color: activeTab === 'locations' ? '#1e40af' : '#6b7280',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s ease',
              borderBottom: activeTab === 'locations' ? '3px solid #3b82f6' : '3px solid transparent'
            }}
          >
            <MapPin size={18} />
            Saved Locations
          </button>
          <button
            onClick={() => setActiveTab('appearance')}
            style={{
              flex: 1,
              padding: '1rem 1.5rem',
              backgroundColor: activeTab === 'appearance' ? '#eff6ff' : 'transparent',
              color: activeTab === 'appearance' ? '#1e40af' : '#6b7280',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s ease',
              borderBottom: activeTab === 'appearance' ? '3px solid #3b82f6' : '3px solid transparent'
            }}
          >
            <Palette size={18} />
            Appearance
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '2rem' }}>
          {activeTab === 'booths' && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '0.75rem',
              padding: '1.5rem',
              border: '2px solid #bfdbfe'
            }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
                marginBottom: '1.5rem'
            }}>
              <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  color: '#1e40af',
                margin: 0
              }}>Booth Configurations</h3>
              <button
                onClick={addNewBooth}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                    padding: '0.5rem 1rem',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                    borderRadius: '0.5rem',
                  cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
              >
                  <Plus size={16} style={{ marginRight: '0.5rem' }} />
                Add Booth
              </button>
            </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {Object.entries(localSettings.booths).map(([key, booth]) => (
                <div key={key} style={{
                    border: '2px solid #e0e7ff',
                    borderRadius: '0.75rem',
                    overflow: 'hidden',
                    backgroundColor: '#fafbff'
                }}>
                  {/* Booth Header */}
                  <div 
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                        padding: '1rem 1.5rem',
                        backgroundColor: expandedBooth === key ? '#eff6ff' : '#fafbff',
                      cursor: 'pointer',
                        borderBottom: expandedBooth === key ? '2px solid #e0e7ff' : 'none',
                        transition: 'all 0.2s ease'
                    }}
                    onClick={() => setExpandedBooth(expandedBooth === key ? null : key)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <input
                        type="text"
                        value={booth.name}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleBoothChange(key, 'name', e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          border: 'none',
                          background: 'transparent',
                            fontSize: '1rem',
                            fontWeight: '600',
                            color: '#1e40af',
                          outline: 'none',
                          width: '100%'
                        }}
                      />
                    </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>
                          Setup: {booth.setupTime}m | Breakdown: {booth.breakdownTime}m
                      </span>
                      {Object.keys(localSettings.booths).length > 1 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteBooth(key);
                          }}
                          style={{
                            color: '#dc2626',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                              padding: '0.5rem',
                              borderRadius: '0.25rem',
                              transition: 'all 0.2s ease'
                          }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#fee2e2'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {expandedBooth === key && (
                      <div style={{ padding: '1.5rem', backgroundColor: '#eff6ff' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                        <div>
                          <label style={{
                            display: 'block',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              color: '#1e40af',
                              marginBottom: '0.5rem'
                          }}>
                              Setup Time (minutes)
                          </label>
                          <input
                            type="number"
                            value={booth.setupTime}
                            onChange={(e) => handleBoothChange(key, 'setupTime', parseInt(e.target.value) || 0)}
                            min="1"
                            max="180"
                            style={{
                              width: '100%',
                                padding: '0.75rem',
                                border: '2px solid #bfdbfe',
                                borderRadius: '0.5rem',
                                fontSize: '1rem',
                                outline: 'none',
                                transition: 'all 0.2s ease'
                              }}
                              onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                              onBlur={(e) => e.currentTarget.style.borderColor = '#bfdbfe'}
                          />
                        </div>
                        <div>
                          <label style={{
                            display: 'block',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              color: '#1e40af',
                              marginBottom: '0.5rem'
                          }}>
                              Breakdown Time (minutes)
                          </label>
                          <input
                            type="number"
                            value={booth.breakdownTime}
                            onChange={(e) => handleBoothChange(key, 'breakdownTime', parseInt(e.target.value) || 0)}
                            min="1"
                            max="120"
                            style={{
                              width: '100%',
                                padding: '0.75rem',
                                border: '2px solid #bfdbfe',
                                borderRadius: '0.5rem',
                                fontSize: '1rem',
                                outline: 'none',
                                transition: 'all 0.2s ease'
                              }}
                              onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                              onBlur={(e) => e.currentTarget.style.borderColor = '#bfdbfe'}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
                     )}

          {activeTab === 'locations' && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '0.75rem',
              padding: '1.5rem',
              border: '2px solid #bfdbfe'
            }}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                color: '#1e40af',
                margin: '0 0 1.5rem 0'
              }}>Saved Locations</h3>
              
              {localSettings.savedLocations.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '2rem',
                  color: '#6b7280'
                }}>
                  <MapPin size={48} style={{ margin: '0 auto 1rem', color: '#9ca3af' }} />
                  <p style={{ margin: 0, fontSize: '1rem' }}>No saved locations yet</p>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>
                    Use the bookmark buttons next to location inputs to save locations
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {localSettings.savedLocations.map((location) => (
                    <div key={location.id} style={{
                      border: '2px solid #e0e7ff',
                      borderRadius: '0.75rem',
                      padding: '1.5rem',
                      backgroundColor: '#fafbff'
                    }}>
                      {editingLocation === location.id ? (
                        <div>
                          <div style={{ marginBottom: '1rem' }}>
                            <label style={{
                              display: 'block',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              color: '#1e40af',
                              marginBottom: '0.5rem'
                            }}>
                              Location Name
                            </label>
                            <input
                              type="text"
                              value={editLocationName}
                              onChange={(e) => setEditLocationName(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '2px solid #bfdbfe',
                                borderRadius: '0.5rem',
                                fontSize: '1rem',
                                outline: 'none'
                              }}
                            />
                          </div>
                          <div style={{ marginBottom: '1rem' }}>
                            <label style={{
                              display: 'block',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              color: '#1e40af',
                              marginBottom: '0.5rem'
                            }}>
                              Address
                            </label>
                            <input
                              type="text"
                              value={editLocationAddress}
                              onChange={(e) => setEditLocationAddress(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '2px solid #bfdbfe',
                                borderRadius: '0.5rem',
                                fontSize: '1rem',
                                outline: 'none'
                              }}
                            />
                          </div>
                          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                            <button
                              onClick={cancelLocationEdit}
                              style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: '#f3f4f6',
                                color: '#374151',
                                border: 'none',
                                borderRadius: '0.5rem',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                fontWeight: '600'
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={saveLocationEdit}
                              style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.5rem',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                fontWeight: '600'
                              }}
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                            <div style={{ flex: 1 }}>
                              <h4 style={{
                                fontSize: '1rem',
                                fontWeight: '600',
                                color: '#1e40af',
                                margin: '0 0 0.5rem 0'
                              }}>
                                {location.name}
                              </h4>
                              <p style={{
                                fontSize: '0.875rem',
                                color: '#6b7280',
                                margin: 0,
                                lineHeight: '1.4'
                              }}>
                                {location.address}
                              </p>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem' }}>
                              <button
                                onClick={() => startEditingLocation(location)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '2rem',
                                  height: '2rem',
                                  backgroundColor: '#eff6ff',
                                  color: '#3b82f6',
                                  border: '1px solid #bfdbfe',
                                  borderRadius: '0.375rem',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.backgroundColor = '#dbeafe';
                                  e.currentTarget.style.color = '#1d4ed8';
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.backgroundColor = '#eff6ff';
                                  e.currentTarget.style.color = '#3b82f6';
                                }}
                                title="Edit location"
                              >
                                <Edit3 size={14} />
                              </button>
                              <button
                                onClick={() => deleteLocation(location.id)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '2rem',
                                  height: '2rem',
                                  backgroundColor: '#fef2f2',
                                  color: '#dc2626',
                                  border: '1px solid #fecaca',
                                  borderRadius: '0.375rem',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.backgroundColor = '#fee2e2';
                                  e.currentTarget.style.color = '#b91c1c';
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.backgroundColor = '#fef2f2';
                                  e.currentTarget.style.color = '#dc2626';
                                }}
                                title="Delete location"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                          <div style={{
                            fontSize: '0.75rem',
                            color: '#9ca3af',
                            borderTop: '1px solid #e5e7eb',
                            paddingTop: '0.75rem'
                          }}>
                            Saved on {new Date(location.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'appearance' && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '0.75rem',
              padding: '1.5rem',
              border: '2px solid #bfdbfe'
            }}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                color: '#1e40af',
                margin: '0 0 1.5rem 0'
              }}>Background Color</h3>
              
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#1e40af',
                  marginBottom: '0.5rem'
                }}>
                  Custom Color
                </label>
                <input
                  type="color"
                  value={localSettings.backgroundColor}
                  onChange={(e) => handleBackgroundColorChange(e.target.value)}
                  style={{
                    width: '100%',
                    height: '3rem',
                    border: '2px solid #bfdbfe',
                    borderRadius: '0.5rem',
                    cursor: 'pointer'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#1e40af',
                  marginBottom: '1rem'
                }}>
                  Preset Colors
                </label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '0.75rem'
                }}>
                  {backgroundColorPresets.map((preset) => (
                    <button
                      key={preset.color}
                      onClick={() => handleBackgroundColorChange(preset.color)}
                      style={{
                        padding: '0.75rem',
                        backgroundColor: preset.color,
                        border: localSettings.backgroundColor === preset.color ? '3px solid #3b82f6' : '2px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        color: '#374151',
                        transition: 'all 0.2s ease',
                        minHeight: '3rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onMouseOver={(e) => {
                        if (localSettings.backgroundColor !== preset.color) {
                          e.currentTarget.style.borderColor = '#9ca3af';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (localSettings.backgroundColor !== preset.color) {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                        }
                      }}
                    >
                      {preset.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1.5rem 2rem',
          borderTop: '2px solid #bfdbfe',
          backgroundColor: '#eff6ff',
          borderRadius: '0 0 1rem 1rem'
        }}>
          <button
            onClick={handleReset}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0.75rem 1.25rem',
              color: '#6b7280',
              background: 'white',
              border: '2px solid #d1d5db',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#f3f4f6';
              e.currentTarget.style.color = '#374151';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.color = '#6b7280';
            }}
          >
            <RotateCcw size={16} style={{ marginRight: '0.5rem' }} />
            Reset All
          </button>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={handleClose}
              style={{
                padding: '0.75rem 1.25rem',
                color: '#6b7280',
                background: 'white',
                border: '2px solid #d1d5db',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
                e.currentTarget.style.color = '#374151';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.color = '#6b7280';
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0.75rem 1.25rem',
                backgroundColor: hasChanges ? '#10b981' : '#9ca3af',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: hasChanges ? 'pointer' : 'not-allowed',
                fontSize: '0.875rem',
                fontWeight: '600',
                opacity: hasChanges ? 1 : 0.6,
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                if (hasChanges) e.currentTarget.style.backgroundColor = '#059669';
              }}
              onMouseOut={(e) => {
                if (hasChanges) e.currentTarget.style.backgroundColor = '#10b981';
              }}
            >
              <Save size={16} style={{ marginRight: '0.5rem' }} />
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal; 